import { useEffect, useMemo, useState } from "react";
import type { LineConfig } from "../../../components/Charts";
import chartDataFetcher, { type P3RawDataPoint } from "../../../services/api/ChartDataFetch";
import { CHART_DIMENSION_PALETTE } from "../../../constants/chartColors";

export interface P3FilterValues {
  yearRange?: [number, number];
}

export interface P3ChartPoint extends Record<string, string | number> {
  any: number;
}

const ESCENARI_CONFIG: Record<string, { label: string; color: string }> = {
  pessimista: { label: "Pessimista (α−σ)", color: CHART_DIMENSION_PALETTE[4] },
  base:       { label: "Base (mitjana)",   color: CHART_DIMENSION_PALETTE[0] },
  optimista:  { label: "Optimista (α+σ)",  color: CHART_DIMENSION_PALETTE[6] },
};

const LINE_ORDER = ["pessimista", "base", "optimista"];

function pivotByEscenari(data: P3RawDataPoint[]): P3ChartPoint[] {
  const anys = Array.from(new Set(data.map((d) => d.any))).sort((a, b) => a - b);

  return anys.map((any) => {
    const point: P3ChartPoint = { any };
    data
      .filter((d) => d.any === any)
      .forEach((d) => {
        const cfg = ESCENARI_CONFIG[d.mode_escenari];
        if (cfg) point[cfg.label] = d.desajust;
      });
    return point;
  });
}

function buildLines(): LineConfig[] {
  return LINE_ORDER.map((mode) => {
    const cfg = ESCENARI_CONFIG[mode] as { label: string; color: string };
    return { key: cfg.label, label: cfg.label, color: cfg.color };
  });
}

export function useP3Data(filters: P3FilterValues) {
  const [rawData, setRawData] = useState<P3RawDataPoint[]>(
    () => chartDataFetcher.getCachedSync<P3RawDataPoint[]>("p3") ?? []
  );
  const [loading, setLoading] = useState(
    () => chartDataFetcher.getCachedSync<P3RawDataPoint[]>("p3") === null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (chartDataFetcher.getCachedSync("p3") !== null) return;

    let cancelled = false;

    setLoading(true);
    setError(null);

    chartDataFetcher
      .getP3Data()
      .then((data) => { if (!cancelled) setRawData(data); })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Error carregant dades");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const yearFiltered = useMemo(() => {
    if (!filters.yearRange) return rawData;
    const [min, max] = filters.yearRange;
    return rawData.filter((d) => d.any >= min && d.any <= max);
  }, [rawData, filters.yearRange]);

  const chartData = useMemo(() => pivotByEscenari(yearFiltered), [yearFiltered]);
  const lines = useMemo(() => buildLines(), []);

  const yearRange = useMemo<[number, number]>(() => {
    if (rawData.length === 0) return [2025, 2045];
    const anys = rawData.map((d) => d.any);
    return [Math.min(...anys), Math.max(...anys)];
  }, [rawData]);

  return { chartData, lines, yearRange, loading, error };
}
